import { getMessages, getTasks, markMessageAsRead } from '@src/api/messageTask';
import { selectIsAuthenticated } from '@src/store/slices/auth';
import type { Message, Task } from '@src/types/shared/api/message_task_api';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MESSAGE_TYPE_COLORS,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
} from '../components/common/message_panel/MessagePanelConstants';

export const useMessageTaskData = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [messages, setMessages] = useState<Message[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingTaskCount, setPendingTaskCount] = useState(0);

  // データ取得
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    async function fetchData(): Promise<void> {
      try {
        const [messagesResponse, tasksResponse] = await Promise.all([getMessages(), getTasks()]);

        setMessages(messagesResponse.data.messages);
        setUnreadCount(messagesResponse.data.unreadCount);

        setTasks(tasksResponse.data.tasks);
        setPendingTaskCount(tasksResponse.data.pendingCount + tasksResponse.data.inProgressCount);
      } catch (error) {
        console.error('メッセージ・タスクデータの取得に失敗しました:', error);
      }
    }

    fetchData();
  }, [isAuthenticated]);

  const handleMessageClick = useCallback(async (messageId: string) => {
    await markMessageAsRead({ messageId });

    setMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? { ...msg, isRead: true } : msg))
    );

    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const getMessageColor = (type: Message['type']): string =>
    MESSAGE_TYPE_COLORS[type] || MESSAGE_TYPE_COLORS.info;

  const getTaskColor = (status: Task['status']): string =>
    TASK_STATUS_COLORS[status] || TASK_STATUS_COLORS.pending;

  const getTaskStatusText = (status: Task['status']): string => TASK_STATUS_LABELS[status] || '';

  return {
    messages,
    tasks,
    unreadCount,
    pendingTaskCount,
    handleMessageClick,
    getMessageColor,
    getTaskColor,
    getTaskStatusText,
  };
};
